export class GravityWell {
  // Smoothed position (used by physics)
  public x = 0
  public y = 0
  public z = 0
  public surfaceRadius = 50

  // Raw input values (before smoothing)
  private rawX = 0
  private rawY = 0
  private rawRadius = 50

  // EMA smoothing parameters (half-life in seconds)
  private readonly POSITION_HALF_LIFE = 0.08 // 80ms - responsive but smooth
  private readonly RADIUS_HALF_LIFE = 0.15 // 150ms - slower for stability

  public mass = 3000
  public glowRadius = 150
  private active = false
  private lastUpdateTime = 0

  // Hand velocity (calculated from raw position changes)
  public vx = 0
  public vy = 0
  private prevRawX = 0
  private prevRawY = 0

  /**
   * EMA smoothing factor from half-life
   * alpha = 1 - exp(-dt * ln(2) / halfLife)
   */
  private getAlpha(dt: number, halfLife: number): number {
    if (dt <= 0 || halfLife <= 0) return 1
    return 1 - Math.exp(-dt * Math.LN2 / halfLife)
  }

  public setPosition(x: number, y: number, z: number, size: number = 0.2): void {
    const now = performance.now() / 1000
    const dt = this.lastUpdateTime > 0 ? now - this.lastUpdateTime : 0.016
    this.lastUpdateTime = now

    // Calculate velocity from raw position change
    if (this.active) {
      this.vx = x - this.prevRawX
      this.vy = y - this.prevRawY
    }
    this.prevRawX = this.rawX
    this.prevRawY = this.rawY

    // Store raw values
    this.rawX = x
    this.rawY = y
    this.z = z

    // Calculate raw surface radius
    // Hand size typically ranges from 0.15 (far) to 0.4 (close)
    // Map to surface radius of 15px (far) to 75px (close)
    this.rawRadius = 15 + size * 150

    // Apply EMA smoothing
    const posAlpha = this.getAlpha(dt, this.POSITION_HALF_LIFE)
    const radAlpha = this.getAlpha(dt, this.RADIUS_HALF_LIFE)

    // First frame: initialize to raw values
    if (!this.active) {
      this.x = x
      this.y = y
      this.surfaceRadius = this.rawRadius
    } else {
      // Smooth transition
      this.x += (this.rawX - this.x) * posAlpha
      this.y += (this.rawY - this.y) * posAlpha
      this.surfaceRadius += (this.rawRadius - this.surfaceRadius) * radAlpha
    }

    // Mass based on depth (closer hand = stronger pull)
    this.mass = 2500 + z * 1500
  }

  public setActive(active: boolean): void {
    if (active && !this.active) {
      // Just became active - reset smoothed values to current raw
      this.x = this.rawX
      this.y = this.rawY
      this.surfaceRadius = this.rawRadius
      this.lastUpdateTime = performance.now() / 1000
    }
    this.active = active
  }

  public isActive(): boolean {
    return this.active
  }

  /**
   * Get distance to particle
   */
  public getDistance(px: number, py: number): number {
    const dx = this.x - px
    const dy = this.y - py
    return Math.sqrt(dx * dx + dy * dy)
  }

  /**
   * Calculate glow intensity based on proximity (0 to 1)
   */
  public getGlowIntensity(px: number, py: number): number {
    if (!this.active) return 0
    const dist = this.getDistance(px, py)
    if (dist > this.glowRadius) return 0
    return Math.pow(1 - dist / this.glowRadius, 1.5)
  }

  /**
   * Get hand velocity for physics calculations
   */
  public getVelocity(): { vx: number; vy: number } {
    return { vx: this.vx, vy: this.vy }
  }
}
